import { useEffect, useRef } from "react";
import { SettingsPanel } from "./SettingsPanel.js";
import type { SetupPaneProps } from "./SetupPane.js";

/**
 * The settings, over the top of the window (one-window-polish ticket 05).
 *
 * **This codebase's first dialog, in a codebase that has argued against them.**
 * `RecoveryPanel`'s delete button says a modal "is the kind of prompt people
 * dismiss without reading, and on the desktop it would steal focus from a window
 * that might be counting into a take". That argument is about a modal that
 * *interrupts*: one that appears unbidden, over a decision you were not making,
 * and is answered by whichever button the reflex reaches. This is the opposite
 * shape — a screen you asked for by pressing the gear, and can leave. Nothing
 * opens it but the gear.
 *
 * The half of the objection that still bites is the *dismissal*, so:
 *
 * - **A click on the backdrop does not close it.** The two things most likely to
 *   be open in here are a half-typed URL and a key rebinding waiting for its
 *   keypress, and both are lost by a stray click on the way past. There is no
 *   click handler on the backdrop at all — the absence *is* the feature.
 * - **Escape and the `✕` do**, and so does the gear, which toggles.
 *
 * **Which listener gets Escape is the fiddly part, and there are three of them.**
 * This one is on `window`, in the bubble phase, and that is a choice against two
 * alternatives:
 *
 * - *Not the capture phase.* `ShortcutPanel` registers a capture-phase listener
 *   on `window` while a rebinding is pending, so that the key being *chosen* is
 *   never read as a key being *pressed* — and Escape there means "leave the
 *   binding as it was". It stops propagation, so it wins over this, which is
 *   exactly right: the half-finished rebind is one of the two things this dialog
 *   refuses to throw away. A capture listener here would fire first and bin it.
 * - *Not the dialog's own subtree.* That would have let this stop propagation
 *   before the shell saw the key, but only while focus was inside the dialog —
 *   and there is no focus trap here, so a Tab too many would silently disarm the
 *   only key that closes the thing.
 *
 * Which leaves the shell's own `useShortcuts("desktop", …)`, also on `window`
 * and also bubbling, registered first and `preventDefault`ing anything it
 * matches. It matches nothing:
 * `test/escape-belongs-to-the-modal.test.ts` holds the desktop table to having
 * no command on Escape, which is the assumption made checkable rather than made.
 */
export function SettingsModal({
  onClose,
  disabled,
  setup,
}: {
  onClose: () => void;
  /** True while a take is in progress. Passed through to the screens that care —
   * see {@link SettingsPanel}. */
  disabled: boolean;
  /** The setup pane's props, unchanged and unwrapped, so the Script tab renders
   * the same component the window itself renders when nothing is loaded. */
  setup: SetupPaneProps;
}): JSX.Element {
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Focus in on open, and back where it came from on close — which is the gear,
  // since the gear is the only way in and a click focuses the button it lands
  // on. Remembering what was focused rather than being handed the gear keeps the
  // dialog from knowing anything about what opened it.
  useEffect(() => {
    const returnTo = document.activeElement;
    dialog.current?.focus();
    return () => {
      if (returnTo instanceof HTMLElement) returnTo.focus();
    };
  }, []);

  return (
    // No `onClick`. That absence is the feature — see above.
    <div className="modal-backdrop">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        tabIndex={-1}
        ref={dialog}
      >
        <header className="modal-header">
          <h2 id="settings-modal-title">Settings</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close settings"
            title="Close settings"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="modal-body">
          <SettingsPanel disabled={disabled} setup={setup} />
        </div>
      </div>
    </div>
  );
}
